/**
 * MobilePreview - Mobile app preview orchestrator
 *
 * Single Responsibility: Orchestrates the mobile preview feature - manages navigation state,
 * audio playback, and coordinates between the device frame and screen content.
 *
 * Composed of:
 * - MobilePreviewFrame (device chrome and container)
 * - PreviewScreens (screen content components)
 */
package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.preview.*
import kotlinx.coroutines.launch
import org.w3c.dom.Audio

@Composable
fun MobilePreview(apiClient: ApiClient, isVisible: Boolean, onClose: () -> Unit) {
    if (!isVisible) return

    var screen by remember { mutableStateOf<PreviewScreen>(PreviewScreen.Home) }
    var previousScreen by remember { mutableStateOf<PreviewScreen?>(null) }
    var currentlyPlaying by remember { mutableStateOf<com.castly.admin.dto.EpisodeResponse?>(null) }
    var currentContent by remember { mutableStateOf<com.castly.admin.dto.ContentResponse?>(null) }
    var audioElement by remember { mutableStateOf<Audio?>(null) }
    var isPlaying by remember { mutableStateOf(false) }
    var urlExpiresAt by remember { mutableStateOf<Long?>(null) }
    var selectedLanguage by remember { mutableStateOf("en") }
    var currentUserId by remember { mutableStateOf(apiClient.getPreviewUserId()) }
    val scope = rememberCoroutineScope()

    // Cleanup audio on unmount
    DisposableEffect(Unit) {
        onDispose {
            audioElement?.pause()
        }
    }

    // Auto-refresh streaming URL before expiration (for podcasts)
    LaunchedEffect(currentlyPlaying, urlExpiresAt) {
        currentlyPlaying?.let { episode ->
            while (true) {
                kotlinx.coroutines.delay(30000) // Check every 30 seconds

                urlExpiresAt?.let { expiresAt ->
                    val now = kotlin.js.Date.now() / 1000
                    val timeUntilExpiry = expiresAt - now

                    if (timeUntilExpiry < 300) {
                        try {
                            console.log("[DEBUG] Refreshing streaming URL (expires in \${timeUntilExpiry}s)")
                            when (val result = apiClient.getEpisodeStreamingUrlV1(episode.id)) {
                                is ApiClient.ApiResult.Success -> {
                                    val response = result.data
                                    audioElement?.let { audio ->
                                        val currentTime = audio.currentTime
                                        audio.src = response.streamingUrl
                                        audio.currentTime = currentTime
                                        if (isPlaying) audio.play()
                                    }

                                    urlExpiresAt = now.toLong() + response.expiresIn
                                    console.log("[DEBUG] Streaming URL refreshed successfully")
                                }
                                is ApiClient.ApiResult.Error -> {
                                    console.error("[ERROR] Failed to refresh streaming URL: ${result.message}")
                                }
                            }
                        } catch (e: Exception) {
                            console.error("[ERROR] Failed to refresh streaming URL:", e)
                        }
                    }
                }
            }
        }
    }

    // Report stream progress every 5 seconds during playback (T050)
    LaunchedEffect(currentlyPlaying, isPlaying) {
        if (isPlaying && currentlyPlaying != null) {
            while (true) {
                kotlinx.coroutines.delay(5000)
                if (!isPlaying || currentlyPlaying == null) break
                audioElement?.let { audio ->
                    val positionSeconds = audio.currentTime.toInt()
                    val durationSeconds = if (audio.duration.isFinite()) audio.duration.toInt() else null
                    try {
                        apiClient.reportStreamProgress(
                            episodeId = currentlyPlaying!!.id,
                            positionSeconds = positionSeconds,
                            durationSeconds = durationSeconds
                        )
                    } catch (e: Exception) {
                        console.error("[DEBUG] Failed to report stream progress:", e)
                    }
                }
            }
        }
    }

    // Navigation handler (T044 - Updated to use ContentResponse)
    fun navigateBack() {
        screen = when (screen) {
            is PreviewScreen.PodcastDetail -> {
                val prev = previousScreen
                previousScreen = null
                prev ?: PreviewScreen.Podcasts
            }
            is PreviewScreen.Episodes -> PreviewScreen.PodcastDetail((screen as PreviewScreen.Episodes).content)
            is PreviewScreen.Player -> PreviewScreen.Episodes((screen as PreviewScreen.Player).content)
            is PreviewScreen.AudiobookDetail -> {
                val prev = previousScreen
                previousScreen = null
                prev ?: PreviewScreen.Audiobooks
            }
            is PreviewScreen.AudiobookEpisodes -> PreviewScreen.AudiobookDetail((screen as PreviewScreen.AudiobookEpisodes).content)
            is PreviewScreen.AudiobookPlayer -> PreviewScreen.AudiobookEpisodes((screen as PreviewScreen.AudiobookPlayer).content)
            else -> PreviewScreen.Home
        }
    }

    // Language toggle handler
    fun toggleLanguage() {
        selectedLanguage = if (selectedLanguage == "en") "ar" else "en"
    }

    // Tab change handler (T044 - Updated for unified state)
    fun onTabChange(newScreen: PreviewScreen) {
        // Clear playing state when switching tabs
        audioElement?.pause()
        currentlyPlaying = null
        currentContent = null
        isPlaying = false
        screen = newScreen
    }

    // Unified play episode handler (T044-T045 - Works for both podcasts and audiobooks)
    fun playEpisode(episode: com.castly.admin.dto.EpisodeResponse, content: com.castly.admin.dto.ContentResponse) {
        currentlyPlaying = episode
        currentContent = content
        audioElement?.pause()

        scope.launch {
            try {
                console.log("[DEBUG] Requesting streaming URL for episode: \${episode.id}")
                when (val result = apiClient.getEpisodeStreamingUrlV1(episode.id)) {
                    is ApiClient.ApiResult.Success -> {
                        val streamingResponse = result.data
                        console.log("[DEBUG] Got streaming URL: \${streamingResponse.streamingUrl}")

                        val now = kotlin.js.Date.now() / 1000
                        urlExpiresAt = now.toLong() + streamingResponse.expiresIn
                        console.log("[DEBUG] URL expires at: \$urlExpiresAt (in \${streamingResponse.expiresIn}s)")

                        val audio = Audio()
                        audio.src = streamingResponse.streamingUrl
                        console.log("[DEBUG] Audio src set to presigned URL")

                        val playPromise = audio.play()
                        playPromise?.catch { error ->
                            console.error("Failed to play audio:", error)
                            isPlaying = false
                        }?.then {
                            console.log("Audio playback started successfully")
                            isPlaying = true
                        }

                        audioElement = audio
                    }
                    is ApiClient.ApiResult.Error -> {
                        console.error("Failed to get streaming URL: ${result.message}")
                        isPlaying = false
                    }
                }
            } catch (e: Exception) {
                console.error("Failed to get streaming URL:", e)
                isPlaying = false
            }
        }

        // Navigate to appropriate player screen based on content type
        screen = if (content.type == "podcast") {
            PreviewScreen.Player(content, episode)
        } else {
            PreviewScreen.AudiobookPlayer(content, episode)
        }
    }

    // Play/pause handler
    fun togglePlayPause() {
        audioElement?.let { audio ->
            if (isPlaying) audio.pause() else audio.play()
            isPlaying = !isPlaying
        }
    }

    MobilePreviewFrame(
        isVisible = isVisible,
        currentScreen = screen,
        selectedLanguage = selectedLanguage,
        currentUserId = currentUserId,
        onNavigateBack = { navigateBack() },
        onToggleLanguage = { toggleLanguage() },
        onChangeUserId = { newId ->
            apiClient.setPreviewUserId(newId)
            currentUserId = newId
            // Reset to home screen to refresh all data with new user context
            audioElement?.pause()
            currentlyPlaying = null
            currentContent = null
            isPlaying = false
            screen = PreviewScreen.Home
        },
        onClose = onClose,
        onTabChange = { onTabChange(it) },
        screenContent = {
            when (val s = screen) {
                is PreviewScreen.Home -> HomeScreen(apiClient, selectedLanguage, onNavigateToContinueWatching = {
                    screen = PreviewScreen.ContinueWatchingList
                }) { content ->
                    previousScreen = screen
                    screen = if (content.type == "audiobook") {
                        PreviewScreen.AudiobookDetail(content)
                    } else {
                        PreviewScreen.PodcastDetail(content)
                    }
                }
                is PreviewScreen.Podcasts -> PodcastsScreen(apiClient, selectedLanguage) { content ->
                    screen = PreviewScreen.PodcastDetail(content)
                }
                is PreviewScreen.PodcastDetail -> ContentDetailScreen(s.content, selectedLanguage, apiClient = apiClient, onViewEpisodes = {
                    screen = PreviewScreen.Episodes(s.content)
                })
                is PreviewScreen.Episodes -> EpisodesScreen(apiClient, s.content, selectedLanguage) { episode ->
                    playEpisode(episode, s.content)
                }
                is PreviewScreen.Player -> PlayerScreen(
                    content = s.content,
                    episode = s.episode,
                    selectedLanguage = selectedLanguage,
                    isPlaying = isPlaying,
                    onPlayPause = { togglePlayPause() }
                )
                is PreviewScreen.Audiobooks -> AudiobooksScreen(apiClient, selectedLanguage) { content ->
                    screen = PreviewScreen.AudiobookDetail(content)
                }
                is PreviewScreen.AudiobookDetail -> ContentDetailScreen(s.content, selectedLanguage, apiClient = apiClient, onViewEpisodes = {
                    screen = PreviewScreen.AudiobookEpisodes(s.content)
                })
                is PreviewScreen.AudiobookEpisodes -> AudiobookEpisodesScreen(apiClient, s.content, selectedLanguage) { episode ->
                    playEpisode(episode, s.content)
                }
                is PreviewScreen.AudiobookPlayer -> AudiobookPlayerScreen(
                    content = s.content,
                    episode = s.episode,
                    selectedLanguage = selectedLanguage,
                    isPlaying = isPlaying,
                    onPlayPause = { togglePlayPause() }
                )
                is PreviewScreen.MyLikes -> PreviewMyLikesView(apiClient, selectedLanguage) { content ->
                    previousScreen = screen
                    screen = if (content.type == "audiobook") {
                        PreviewScreen.AudiobookDetail(content)
                    } else {
                        PreviewScreen.PodcastDetail(content)
                    }
                }
                is PreviewScreen.ContinueWatchingList -> PreviewContinueWatchingListView(apiClient, selectedLanguage) { item ->
                    // Navigate to content detail when tapping a continue watching item
                    val languageCode = if (selectedLanguage == "en") "eng" else "ara"
                    scope.launch {
                        when (val result = apiClient.getContentById(item.contentId, languageCode)) {
                            is ApiClient.ApiResult.Success -> {
                                previousScreen = screen
                                val content = result.data
                                screen = if (content.type == "audiobook") {
                                    PreviewScreen.AudiobookDetail(content)
                                } else {
                                    PreviewScreen.PodcastDetail(content)
                                }
                            }
                            is ApiClient.ApiResult.Error -> console.error("Failed to load content: ${result.message}")
                        }
                    }
                }
            }
        },
            miniPlayer = if (currentlyPlaying != null && currentContent != null &&
                           screen !is PreviewScreen.Player && screen !is PreviewScreen.AudiobookPlayer) {
            {
                MiniPlayerUnified(
                    episode = currentlyPlaying!!,
                    content = currentContent!!,
                    selectedLanguage = selectedLanguage,
                    isPlaying = isPlaying,
                    onPlayPause = { togglePlayPause() },
                    onClick = {
                        val content = when (val s = screen) {
                            is PreviewScreen.PodcastDetail -> s.content
                            is PreviewScreen.Episodes -> s.content
                            is PreviewScreen.AudiobookDetail -> s.content
                            is PreviewScreen.AudiobookEpisodes -> s.content
                            else -> null
                        }
                        content?.let {
                            screen = if (it.type == "podcast") {
                                PreviewScreen.Player(it, currentlyPlaying!!)
                            } else {
                                PreviewScreen.AudiobookPlayer(it, currentlyPlaying!!)
                            }
                        }
                    }
                )
            }
        } else null
    )
}
